import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { AppSelect } from '../../../components/common/select';
import api from '../../../api';
import useModalEffect from '../../../hooks/useModalEffect';

const TemplateForm = ({ isOpen, onClose, onSubmit, editingTemplate }) => {
  useModalEffect(isOpen);
  const [formData, setFormData] = useState({
    templateName: '',
    service: '',
    serviceName: '',
    templateContent: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [serviceOptions, setServiceOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch service options from API
  useEffect(() => {
    const fetchServiceOptions = async () => {
      setLoading(true);
      try {
        const res = await api.get('/crm/services/options');
        setServiceOptions(
          (res.data || []).map(service => ({
            id: service.id,
            name: service.label || service.name
          }))
        );
      } catch (err) {
        console.error('Error fetching service options:', err);
        setServiceOptions([]);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchServiceOptions();
    }
  }, [isOpen]);

  ;

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      // Clean up any remaining modal states when component unmounts
      const cleanupModal = () => {
        // Remove modal scroll lock
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('modal-backdrop');
      };
      
      cleanupModal();
    };
  }, []);

  // Reset form when modal opens/closes or editingTemplate changes
  useEffect(() => {
    if (isOpen) {
      if (editingTemplate) {
        // Prefill form for editing
        setFormData({
          templateName: editingTemplate.templateName || '',
          service: editingTemplate.serviceId || '',
          serviceName: editingTemplate.serviceName || '',
          templateContent: editingTemplate.templateContent || ''
        });
      } else {
        // Clear form for new template
        setFormData({
          templateName: '',
          service: '',
          serviceName: '',
          templateContent: ''
        });
      }
      // Clear errors
      setFormErrors({});
    }
  }, [isOpen, editingTemplate]);

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.templateName.trim()) {
      errors.templateName = 'Template name is required';
    }
    if (!formData.service) {
      errors.service = 'Service is required';
    }
    return errors;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length === 0) {
      onSubmit(formData);
      // Form will be reset by parent component after successful submission
    } else {
      setFormErrors(errors);
    }
  };

  const handleModalClose = () => {
    // Reset form when closing
    setFormData({
      templateName: '',
      service: '',
      serviceName: '',
      templateContent: ''
    });
    setFormErrors({});
    onClose();
  };


  // Cleanup effect: reset modal body state when component unmounts.
  useEffect(() => {
    return () => {
      // React owns the backdrop DOM (createPortal); only reset body state here.
      // Remove modal-open class from body
      document.body.classList.remove('modal-open');
    };
  }, []);

  return (
    <>
      <style>{`
        #modal-template-form .inv-section-label {
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #6c7a7d; margin-bottom: 0.75rem;
          display: flex; align-items: center; gap: 0.4rem;
        }
        #modal-template-form .inv-section-label::after {
          content: ''; flex: 1; height: 1px; background: #e9ecef;
        }
        #modal-template-form .inv-footer {
          border-top: 1px solid #e9ecef !important;
          background: #fff;
          padding: 1rem 1.5rem;
          display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem;
          flex-shrink: 0;
        }
        #modal-template-form .btn-inv-save {
          display: inline-flex; align-items: center; gap: 0.4rem;
        }
        #modal-template-form .btn-inv-save:disabled {
          opacity: 0.6; cursor: not-allowed;
        }
        #modal-template-form .inv-required-star { color: #e53935; margin-left: 2px; }
      `}</style>
      {isOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${isOpen ? ' show' : ''}`} style={{ display: isOpen ? 'block' : 'none' }}
        id="modal-template-form"
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {editingTemplate ? 'Edit Template' : 'Add New Template'}
              </h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Template Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${formErrors.templateName ? 'is-invalid' : ''}`}
                    name="templateName"
                    value={formData.templateName}
                    onChange={(e) => handleInputChange('templateName', e.target.value)}
                    placeholder="Enter template name"
                  />
                  {formErrors.templateName && (
                    <div className="invalid-feedback">{formErrors.templateName}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Service <span className="text-danger">*</span></label>
                  <AppSelect
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    options={serviceOptions}
                    placeholder={loading ? "Loading services..." : "Select service"}
                    disabled={loading}
                    dropdownParent="body"
                  />
                  {formErrors.service && (
                    <div className="invalid-feedback">{formErrors.service}</div>
                  )}
                </div>
              </div>
              <div className="inv-footer">
                <span className="text-muted me-auto" style={{ fontSize: '0.75rem' }}>
                  {loading ? 'Loading…' : (
                    <span style={{ color: '#9aa5b1' }}>
                      <span className="inv-required-star">*</span> Required fields
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-inv-save" disabled={loading}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  {editingTemplate ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

TemplateForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  editingTemplate: PropTypes.object
};

export default TemplateForm; 